import { Anchor, Flex } from '@vtex/admin-ui'
import { useFormState } from '@vtex/admin-ui-form'
import { useCallback, useEffect } from 'react'
import { useIntl } from 'react-intl'
import { type UTM } from 'typings'
import {
  CancelButton,
  DismissButton,
  Drawer,
  DrawerContent,
  DrawerFooter,
  DrawerHeader,
  DrawerSubtitle,
  DrawerTitle,
  SaveButton,
  type UseDrawerStateReturn,
} from '../../../Drawer'
import { Coupons } from './Coupons'
import { messages } from './messages'
import styles from './utm.css'
import { UTMField } from './UTMField'

interface Props {
  state: UseDrawerStateReturn
  initialConfig: UTM
  onSave: (config: UTM) => void
}

export function Editor(props: Props) {
  const { state, initialConfig, onSave } = props
  const { utmSource, utmCampaign } = initialConfig

  const isCreating = utmSource.length === 0 && utmCampaign.length === 0

  const { hide } = state

  const { $t } = useIntl()

  const drawerForm = useFormState<UTM>({
    defaultValues: initialConfig,
  })

  const handleSave = async () => {
    onSave(drawerForm.getValues())
    hide()
  }

  const setInitialConfig = useCallback(() => {
    drawerForm.reset(initialConfig)
  }, [drawerForm, initialConfig])

  useEffect(() => {
    state.open && setInitialConfig()
  }, [setInitialConfig, state.open])

  return (
    <Drawer state={state}>
      <DrawerHeader>
        <DrawerTitle>{$t(messages.title)}</DrawerTitle>
        <DrawerSubtitle>
          {$t(messages.subtitle, {
            a: (content: any) => (
              <Anchor target="_blank" href={$t(messages.moreInformation)}>
                {content}
              </Anchor>
            ),
          })}
        </DrawerSubtitle>
        <DismissButton onHide={state.hide} />
      </DrawerHeader>
      <DrawerContent>
        <Flex className={styles.utmFields}>
          <UTMField
            name="utmSource"
            label={$t(messages.utmSource)}
            state={drawerForm}
          />
          <UTMField
            name="utmCampaign"
            label={$t(messages.utmCampaign)}
            state={drawerForm}
          />
        </Flex>
        <Coupons state={drawerForm} />
      </DrawerContent>
      <DrawerFooter>
        <CancelButton onClick={hide} />
        <SaveButton isCreating={isCreating} onClick={handleSave} />
      </DrawerFooter>
    </Drawer>
  )
}
